import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { IntlService } from '@progress/kendo-angular-intl';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { TaskUtility } from 'src/app/task/task.utility';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { Subscription } from 'rxjs';
import { SessionStorageService } from 'ngx-webstorage';

@Component({
  standalone: false,
  selector: 'app-case-detail-summary-task-grid',
  templateUrl: './case-detail-summary-task-grid.component.html',
  styleUrls: ['./case-detail-summary-task-grid.component.scss']
})
export class CaseDetailSummaryTaskGridComponent implements OnDestroy
{
  @Input() public allowAddTask = true;
  @Input() public refreshCaseOnUpdate = false;
  @Output() public refreshCaseDetailsData = new EventEmitter<number>();

  public taskView = new GridViewManager();
  public taskCount: number;
  public priorityDialogOpened = false;
  public taskDueDateOpened = false;
  public pageSizes = [5, 10, 25, 50, 100];
  public windowState = 'default';
  public disableClose = false;

  /**
   * Controls the Task detail modal
   */
  public taskOpened = false;

  /**
   * The select task from the tasks table.
   */
  public selectedTask: any = null;

  /**
   * When true, auto complete if there is no outcomes.
   */
  public autoComplete = false;

  /**
   * Selected task for re-assignment.
   */
  public task = null;

  /**
   * Controls the task assignee dialog.
   */
  public assigneeDialogOpened = false;

  /**
   * Text shorted of the taskname
   */
  public shortTaskName: string;

  /**
   * When creating a new task, indicate the default task assignee.
   */
  public createTaskAssigneeId = '';

  /**
   * Is it a new task?
   */
  public isNewTask = false;

  /**
   * Case model from @Input()
   */
  public caseModel: any;

  /**
   * Messaging subscription
   */
  private messagingSubscription: Subscription;

  constructor(
    private taskUtility: TaskUtility,
    private messengerService: MessengerService,
    public intl: IntlService,
    private appConfigService: AppConfigService,
    private storageService: SessionStorageService)
  {
    this.disableClose = false;
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'disableTaskClose' && message.data)  
        {
          this.disableClose = message.data.buttonState;
        }
        else if (message && message.type && message.type === 'enableReferralCloseButton')
        {
          this.disableClose = false;
        }
      }
    });
  }

  @Input() public set model(data: any)
  {
    // When the new referral dialog is open, we don't want to re-enable the close button until the referral dialog closes.
    // Using a session storage variable as a work around since those components are not linked directly.
    const isNewReferralDialogOpen = this.storageService.retrieve('newReferralDialogOpen') || false;
    if (!isNewReferralDialogOpen)
    {
      this.disableClose = false;
    }
    this.caseModel = data;

    this.taskView = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.taskView.startLoading();

    // Set tje full object for event referral otherwise the complete task will fail
    data.tasks.forEach(t =>
    {
      t.eventReferral = data.eventReferral;
    });

    this.taskCount = data.tasks.length;
    this.taskView.setData(data.tasks);
    this.taskView.setSort([{ field: 'name', dir: 'asc' }]);
    this.taskView.stopLoading();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public taskDataStateChange(state: DataStateChangeEvent): void
  {
    this.taskView.setState(state);
  }

  /**
   * Opens the task detail modal.
   *
   * @param task Task object
   */
  public openTask(task: any, isAutoComplete = false): void
  {
    this.autoComplete = isAutoComplete;
    this.selectedTask = task;
    this.selectedTask.name = TextProcessor.truncateText(this.selectedTask.name, 70);
    this.taskOpened = true;
    document.body.style.overflow = 'hidden';
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskAssigneeDialog(task): void
  {
    this.task = task;
    this.shortTaskName = TextProcessor.truncateText(this.task.name, 35);
    this.assigneeDialogOpened = true;
  }

  /**
   * Updates a task's priority.
   *
   * @param task Selected task for re-prioritize.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskPriority(task): void
  {
    this.task.priorityNumber = task.priorityNumber;
    this.task.priority = task.priority;
    this.task.priorityBadgeClass = this.taskUtility.priorityBadgeClass(task.priority);
    this.priorityDialogOpened = false;
    if (this.refreshCaseOnUpdate)
    {
      this.getCaseDetailsData();
    }
  }

  public closeTaskPriorityDialog(): void
  {
    this.priorityDialogOpened = false;
  }

  /**
   * Updates a task's assignee.
   *
   * @param assignee Task assignee.
   */
  public updateTaskAssignee(assignee): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.closeTaskAssigneeDialog();
    if (this.refreshCaseOnUpdate)
    {
      this.getCaseDetailsData();
    }
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.task = null;
    this.assigneeDialogOpened = false;
  }

  public closeTaskDueDateDialog(): void
  {
    this.task = null;
    this.taskDueDateOpened = false;
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskDueDate(task): void
  {
    this.task.dueDate = task.dueDate;
    this.taskDueDateOpened = false;
    if (this.refreshCaseOnUpdate)
    {
      this.getCaseDetailsData();
    }
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskDueDateDialog(task): void
  {
    this.task = task;
    this.taskDueDateOpened = true;
  }

  /**
   * Sets the selected task, and opens the task priority dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskPriorityDialog(task): void
  {
    this.task = task;
    this.priorityDialogOpened = true;
  }

  /**
   * Event handler when attempting to complete that and No was selected from the confirmation dialog.
   * If the complete dialog was automatically open (autoComplete=true) then the task detail dialog will
   * close automatically.
   *
   * @param taskId Task ID
   */
  public closeTaskDetail(): void
  {
    this.closeTaskDialog();
  }

  public closeTaskDialog(): void
  {
    this.taskOpened = false;
    this.getCaseDetailsData();
    document.body.style.overflow = '';
  }

  /**
   * Open the new task dialog
   */
  public createTask(assigneeId: string = null): void
  {
    this.createTaskAssigneeId = assigneeId;
    this.isNewTask = true;
    this.selectedTask = null;
  }

  /**
   * Method called after a new task has been added.
   */
  public afterTaskAdded(): void
  {
    this.isNewTask = false;
    // send message to update case history timeline (Case UI)
    this.messengerService.sendMessage(new MessageSubject('caseHistoryUpdated'));
  }

  /**
   * Event handler for task completion.
   */
  public onTaskCompleted(): void
  {
    this.closeTaskDialog();
    setTimeout(() => { this.getCaseDetailsData(); }, 8000);
  }

  /**
   * Send message to case-details.component to force a case reload.
   */
  public getCaseDetailsData(): void
  {
    this.messengerService.sendMessage(new MessageSubject('getCaseDetailsData'));
  }
}
